import { CheckCircle } from "@mui/icons-material";
import classnames from "classnames";
import _ from "lodash";
import React from "react";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import localeDates from "../../utils/localeDates";
import moment from "../../utils/moment-timezone-with-data";
import ChevronDownIcon from "../icons/ChevronDown.svg";
import * as styles from "./index.module.scss";
import { get } from "store";
import { overrideOpeningScheduling, filterByOrderSchedulings } from "../../utils/futureOrder";


const getClosingDaysFromOrderScheduling = (branchAvailability, timeZone, servingOptionType, closedDaysInMonthFromBranchClosedDaysOfWeek) => {
  return _.flatten(_.map(_.filter(_.get(branchAvailability, "orderSchedulings"),
    schedule => schedule.active &&
      (schedule.schedulingType === "CLOSING" || schedule.schedulingType === "OPENING") && _.includes(schedule.servingOptionTypes, servingOptionType)),
    schedule => {
      if (schedule.schedulingType === "CLOSING") {
        return _.compact(_.map(_.range(0, moment.tz(_.get(schedule, "scheduleTiming.toDate"), timeZone).diff(moment.tz(_.get(schedule, "scheduleTiming.fromDate"), timeZone)) / (1000 * 60 * 60 * 24)),
          addDays => {
            const day = moment.tz(_.get(schedule, "scheduleTiming.fromDate"), timeZone).add(addDays, "days");
            const hours = _.map(_.range(0, 23), hour => moment.tz(day.toDate(), timeZone).hour(hour));
            if (_.isEmpty(
              overrideOpeningScheduling({
                timeZoneStr: timeZone,
                branchAvailability,
                servingTimeData: { day },
                selectedServingOption: { type: servingOptionType }
              })(filterByOrderSchedulings({
                timeZoneStr: timeZone,
                branchAvailability,
                servingTimeData: { day },
                selectedServingOption: { type: servingOptionType }
              })(hours))
            )) {
              return moment(moment.tz(day, timeZone).format("YYYY-MM-DD")).toDate();
            }
          }));
      } else if (schedule.schedulingType === "OPENING") {
        return _.compact(_.map(_.range(0, moment.tz(_.get(schedule, "scheduleTiming.toDate"), timeZone).diff(moment.tz(_.get(schedule, "scheduleTiming.fromDate"), timeZone)) / (1000 * 60 * 60 * 24)),
          addDays => {
            const day = moment.tz(_.get(schedule, "scheduleTiming.fromDate"), timeZone).add(addDays, "days");
            const hours = _.map(_.range(0, 23), hour => moment.tz(day.toDate(), timeZone).hour(hour));
            //check if the schedule includes the specific day - if so it should be open
            console.log("hours for day", filterByOrderSchedulings({
              timeZoneStr: timeZone,
              branchAvailability,
              servingTimeData: { day },
              selectedServingOption: { type: servingOptionType }
            })(hours));
            if (_.isEmpty(filterByOrderSchedulings({
              timeZoneStr: timeZone,
              branchAvailability,
              servingTimeData: { day },
              selectedServingOption: { type: servingOptionType }
            })(hours))) {
              console.log("Adding day to closedDaysInMonthFromBranchClosedDaysOfWeek", moment.tz(day, timeZone).format("YYYY-MM-DD"));
              return moment(moment.tz(day, timeZone).format("YYYY-MM-DD")).toDate();
            } else {
              console.log("Removing day from closedDaysInMonthFromBranchClosedDaysOfWeek", moment.tz(day, timeZone).format("YYYY-MM-DD"));
              _.remove(closedDaysInMonthFromBranchClosedDaysOfWeek, closed => closed === moment.tz(day, timeZone).format("YYYY-MM-DD"));
            }
          }));
      }
    }
  ));
}

const getClosedDaysFromBranchHours = (branchAvailability, timeZone, weeks) => {
  const from = _.get(branchAvailability, "futureOrderInterval.before") ? moment.tz(_.get(branchAvailability, "futureOrderInterval.before"), timeZone) : moment.tz(timeZone);
  let closedDaysInMonthFromBranchClosedDaysOfWeek = _.flatten(_.map(_.get(branchAvailability, "branchClosedDays"),
    dayOfWeek => {
      const retval = _.map(weeks, week => {
        return moment(from).day(dayOfWeek).add(week, "weeks").format("YYYY-MM-DD");
      });
      return retval;
    }));
  return closedDaysInMonthFromBranchClosedDaysOfWeek;
}

export default function ({
  T,
  appStyles,
  servingOptionTag,
  servingOptionType,
  openCalendar,
  date,
  onDaySelected,
  dateInDisplayFormat,
  onInputClick,
  selectedDate,
  branchAvailability,
  timeZone,
}) {
  const isClosedForToday =
    _.get(branchAvailability, "availability") === "AVAILABLE_LATER" &&
    moment.tz(timeZone).dayOfYear() !==
    moment
      .tz(moment(_.get(branchAvailability, "availableFrom")), timeZone)
      .dayOfYear();

  const renderDay = (day) => {

    const isToday =
      moment.tz(timeZone).dayOfYear() ===
      moment.tz(moment(day), timeZone).dayOfYear();
    const closedForToday = isToday && isClosedForToday;
    const dateStyle = closedForToday
      ? { color: "#D0021B", cursor: "not-allowed" }
      : { bottom: -20 };
    const textStyle = {
      fontSize: 10,
      textAlign: "center",
      position: "absolute",
      left: -25,
      width: 70,
    };
    const cellStyle = { position: "relative" };

    return (
      <div style={cellStyle}>
        <div style={{ ...dateStyle }}>{day.getDate()}</div>
        {closedForToday && (
          <div
            style={{
              ...textStyle,
              color: "#D0021B",
            }}
          >
            {T("Closed")}
          </div>
        )}
        {!closedForToday && isToday && (
          <div style={{ ...textStyle, color: "inherit", fontWeight: "bolder" }}>
            {T("Today")}
          </div>
        )}
      </div>
    );
  };
  const closedForTodayMessage = T(`*${servingOptionTag} is closed today`);
  const isToday =
    moment.tz(moment(selectedDate || new Date()), timeZone).dayOfYear() ===
    moment.tz(timeZone).dayOfYear();

  //get range of weeks between branchAvailability.futureOrderInterval.before to branchAvailability.futureOrderInterval.after
  const weeks = _.range(0, moment.tz(_.get(branchAvailability, "futureOrderInterval.after"), timeZone).diff(moment.tz(_.get(branchAvailability, "futureOrderInterval.before"), timeZone)) / (1000 * 60 * 60 * 24 * 7));

  //get moment days of the month, and check if they are in branchAvailability.branchClosedDays and format to YYYY-MM-DD
  let closedDaysInMonthFromBranchClosedDaysOfWeek = getClosedDaysFromBranchHours(branchAvailability, timeZone, weeks);  
  const closedSchedules = getClosingDaysFromOrderScheduling(branchAvailability, timeZone, servingOptionType, closedDaysInMonthFromBranchClosedDaysOfWeek);

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          paddingTop: 4,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>{T(`${servingOptionTag} Date`)}:</div>
        {isClosedForToday && (
          <div
            style={{ color: "#D0021B", fontSize: "0.7rem" }}
          >{`${closedForTodayMessage}`}</div>
        )}
      </div>
      <div>
        <div
          className={styles.DateInputDesktop}
          style={{
            borderRadius: appStyles.inputBorderRadius,
            ...appStyles.Input,
            ...(appStyles.inputValidColor && {
              border: `1px solid ${appStyles.inputValidColor}`,
            }),
            ...(openCalendar
              ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
              : {
                borderBottomLeftRadius: appStyles.inputBorderRadius,
                borderBottomRightRadius: appStyles.inputBorderRadius,
              }),
          }}
          onClick={onInputClick}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <CheckCircle style={{ color: appStyles.inputValidColor, marginLeft: '14px' }} />
            <div style={{ margin: "0 10px", color: "inherit" }}>
              <span>
                {moment
                  .tz(moment(selectedDate || new Date()), timeZone)
                  .format("MMM D, YYYY")}
              </span>{" "}
              {isToday && <span>{T("(Today)")}</span>}
            </div>
          </div>
          <ChevronDownIcon
            className={classnames(
              styles.ChevronDownIcon,
              openCalendar && styles.CalendarOpened,
            )}
          />
        </div>
        {openCalendar && (
          <div
            className={styles.DayPickerContainer}
            style={{
              borderRadius: appStyles.inputBorderRadius,
              ...appStyles.Input,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              background: appStyles.inputOnCardBackgroundColor,
              color: appStyles.inputOnCardTextColor,
            }}
          >
            <DayPicker
              onDayClick={onDaySelected}
              selectedDays={moment
                .tz(moment(selectedDate || new Date()), timeZone)
                .toDate()}
              months={localeDates[appStyles.locale || "en-US"].MONTHS}
              weekdaysLong={
                localeDates[appStyles.locale || "en-US"].WEEKDAYS_LONG
              }
              weekdaysShort={
                localeDates[appStyles.locale || "en-US"].WEEKDAYS_SHORT
              }
              fromMonth={branchAvailability && branchAvailability.futureOrderInterval.before}
              toMonth={branchAvailability && branchAvailability.futureOrderInterval.after}
              className={classnames(
                styles.DayPicker,
                appStyles.rtl && styles.RTL,
              )}
              modifiers={{
                disabled: [
                  //{ daysOfWeek: branchAvailability?.branchClosedDays },
                  ...(_.concat(closedSchedules || [], closedDaysInMonthFromBranchClosedDaysOfWeek ? 
                    _.map(closedDaysInMonthFromBranchClosedDaysOfWeek, closed => moment.tz(closed, timeZone).toDate()): [])),
                  branchAvailability?.futureOrderInterval,
                  ...(isClosedForToday ? [moment.tz(timeZone).toDate()] : [])
                ],
              }}
              modifiersStyles={{
                selected: { ...appStyles.Button, borderRadius: 100 },
              }}
              firstDayOfWeek={
                _.isNumber(appStyles.firstDayOfWeek)
                  ? appStyles.firstDayOfWeek
                  : localeDates[appStyles.locale || "en-US"].firstDayOfWeek
              }
              month={branchAvailability?.futureOrderInterval.before}
              renderDay={renderDay}
            />
          </div>
        )}
      </div>
    </div>
  );

  
}


